import { PauseCircle } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { cn } from '@/lib/utils'
import { getPaymentsHold, type HoldableSubscription } from '@/utils/payments-hold'

interface PaymentsHoldNoticeProps {
    subscriptions: HoldableSubscription[] | null | undefined
    className?: string
}

const formatDate = (iso: string): string =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })

/**
 * Tells the user that payments for a license are temporarily suspended. The license itself keeps running.
 * Renders nothing when no subscription of the license is on hold.
 */
const PaymentsHoldNotice = ({ subscriptions, className = '' }: PaymentsHoldNoticeProps) => {
    const { onHold, partial, until } = getPaymentsHold(subscriptions)
    if (!onHold) return null

    const title = partial
        ? 'Payments for some subscriptions of this license are temporarily suspended'
        : 'Payments for this license are temporarily suspended'

    return (
        <Alert
            variant="default"
            data-testid="payments-hold-notice"
            className={cn('tw:border-amber-500/40 tw:bg-amber-500/10 tw:text-white', className)}
        >
            <PauseCircle aria-hidden="true" />
            <AlertTitle className="">{title}</AlertTitle>
            <AlertDescription className="tw:text-white/80">
                {until
                    ? `You will not be charged for ${partial ? 'them' : 'it'} until ${formatDate(until)}, when payments resume automatically.`
                    : `You will not be charged for ${partial ? 'them' : 'it'} until payments are resumed.`}
            </AlertDescription>
        </Alert>
    )
}

export default PaymentsHoldNotice
